'use client';
import { getVariantFromSimpleControls } from '@/utils/animation';
import { getCTA } from '@/utils/getCTA';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { isVideoUrl } from '@/utils/isVideoUrl';
import { normalizeSvgEmbedMarkup } from '@/utils/normalizeSvgEmbed';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';
import VideoEmbed from './atoms/VideoEmbed';

const normalizeMediaUrl = (input) => {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.Media ||
            input.media ||
            input.url ||
            input.src ||
            input.value ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
};

const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

const normalizeVideoUrl = (input) => {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.value ||
            input.url ||
            input.src ||
            input.Media ||
            input.media ||
            '';
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
};

const normalizeAnimation = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
};

const normalizeAnimationGroup = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (['media', 'medias', 'image', 'images', 'background', 'bg'].includes(v))
        return 'media';
    if (['box', 'panel', 'card'].includes(v)) return 'box';
    if (['cta', 'ctas', 'ctas-only', 'button', 'buttons'].includes(v))
        return 'ctas';
    if (['all', 'content+media', 'content-media', 'both'].includes(v))
        return 'all';
    return 'content';
};

function MotionMaybe({ enable, k, motionProps, className, style, children }) {
    if (enable) {
        return (
            <motion.div
                key={`${k}-on`}
                className={className}
                style={style}
                {...motionProps}
            >
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '', ...style }}
        >
            {children}
        </div>
    );
}

function BackgroundVideo({
    videoUrl,
    videoPositionStyles,
    className = '',
    onVideoError,
}) {
    const videoWrapperRef = useRef(null);
    const [coverSize, setCoverSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!videoUrl) return;
        const node = videoWrapperRef.current;
        if (!node) return;

        const updateCover = () => {
            const rect = node.getBoundingClientRect();
            if (!rect.width || !rect.height) return;
            const videoAspect = 16 / 9;
            const containerAspect = rect.width / rect.height;
            let width;
            let height;
            if (containerAspect >= videoAspect) {
                width = rect.width;
                height = rect.width / videoAspect;
            } else {
                height = rect.height;
                width = rect.height * videoAspect;
            }
            setCoverSize({ width, height });
        };

        updateCover();
        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(updateCover);
            observer.observe(node);
        } else {
            window.addEventListener('resize', updateCover);
        }

        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateCover);
        };
    }, [videoUrl]);

    if (!videoUrl) return null;

    const width = coverSize.width || '100%';
    const height = coverSize.height || '100%';

    return (
        <div
            className={`pointer-events-none absolute inset-0 -z-10 overflow-hidden ${className}`}
        >
            <div ref={videoWrapperRef} className="absolute inset-0">
                <VideoEmbed
                    url={videoUrl}
                    playing
                    muted
                    loop
                    controls={false}
                    width={width}
                    height={height}
                    playsinline
                    className="absolute"
                    style={videoPositionStyles}
                    onError={onVideoError}
                />
            </div>
        </div>
    );
}

const isTransparentBackground = (value) => {
    if (!value) return true;
    const normalized = value.replace(/\s+/g, '').toLowerCase();
    return (
        normalized === 'transparent' ||
        normalized === 'rgba(0,0,0,0)' ||
        normalized === 'hsla(0,0%,0%,0)'
    );
};

const readRenderedBackground = (node) => {
    if (!node || typeof window === 'undefined') return null;
    const candidates = [
        node.matches?.('.editable-component') ? node : null,
        node.querySelector?.('.editable-component'),
        node,
        ...Array.from(
            node.querySelectorAll?.('section, [data-component]') || []
        ),
    ].filter(Boolean);

    for (const candidate of candidates) {
        const style = window.getComputedStyle(candidate);
        const hasImage =
            style.backgroundImage && style.backgroundImage !== 'none';
        const hasColor = !isTransparentBackground(style.backgroundColor);

        if (hasImage || hasColor) {
            return {
                background: style.background,
            };
        }
    }

    return null;
};

export default function CTAHero(props) {
    const {
        Header,
        Body,
        ImageUrl,
        ImagePosition = '50',
        Video,
        ColorScheme = 'light',
        BackgroundFixed = true,
        Alignment = 'center',
        VerticalSpacing = 'medium',
        Layout = 'Full',
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;
    const topBridgeColorScheme =
        props.PreviousColorScheme || props.__previousColorScheme || ColorScheme;
    const bottomBridgeColorScheme =
        props.NextColorScheme || props.__nextColorScheme || ColorScheme;

    const {
        backgroundColor,
        textColor,
        taglineColor,
        boxTextColor,
        boxColor,
        thirdButton,
        secondaryButton,
        boxThirdButton,
        boxSecondaryButton,
        overlayColor,
        boxBorder,
        backgroundStyle,
        overlayStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);
    const {
        backgroundColor: topBridgeBackgroundColor,
        backgroundStyle: topBridgeBackgroundStyle,
    } = getColorScheme(topBridgeColorScheme, props.GradientDirection);
    const {
        backgroundColor: bottomBridgeBackgroundColor,
        backgroundStyle: bottomBridgeBackgroundStyle,
    } = getColorScheme(bottomBridgeColorScheme, props.GradientDirection);
    const sectionRef = useRef(null);
    const [renderedBridgeBackgrounds, setRenderedBridgeBackgrounds] = useState({
        top: null,
        bottom: null,
    });

    const rawBackgroundImageUrl = (() => {
        const fromMedia = normalizeMediaUrl(props.Media);
        const fromMedias = Array.isArray(props.Medias)
            ? normalizeMediaUrl(
                  props.Medias[0]?.Media ??
                      props.Medias[0]?.media ??
                      props.Medias[0]
              )
            : '';
        const fromImage = normalizeMediaUrl(ImageUrl);
        return fromMedia || fromMedias || fromImage;
    })();

    const normalizedVideoUrl = normalizeVideoUrl(Video);
    const fallbackVideoUrl =
        !normalizedVideoUrl && isVideoUrl(rawBackgroundImageUrl)
            ? rawBackgroundImageUrl
            : '';
    const videoUrl = normalizedVideoUrl || fallbackVideoUrl;
    const backgroundIsEmbed = isEmbedCode(rawBackgroundImageUrl);

    // Only render the video on the client to avoid SSR/CSR mismatch.
    const [isClient, setIsClient] = useState(false);
    const [videoError, setVideoError] = useState(false);
    useEffect(() => setIsClient(true), []);
    useEffect(() => setVideoError(false), [videoUrl]);

    const showVideo = isClient && !!videoUrl && !videoError;
    const isBackgroundFixed =
        typeof BackgroundFixed === 'string'
            ? BackgroundFixed.toLowerCase() === 'true'
            : !!BackgroundFixed;
    const isClear = (ColorScheme || '').trim() === 'clear';
    const isBoxLayout = String(Layout).toLowerCase() === 'box';
    const minHeightClass = isClear
        ? isBoxLayout
            ? ''
            : 'sm:min-h-[300px] md:min-h-[500px] lg:min-h-[700px] xl:min-h-[900px]'
        : '';
    const backgroundImageUrl = fallbackVideoUrl ? '' : rawBackgroundImageUrl;

    useEffect(() => {
        if (!isBoxLayout || !sectionRef.current) {
            setRenderedBridgeBackgrounds({ top: null, bottom: null });
            return undefined;
        }

        let frameId = 0;
        const updateRenderedBridgeBackgrounds = () => {
            const current = sectionRef.current;
            if (!current) return;
            const wrapper = current.closest?.('.edit-component-wrapper');

            setRenderedBridgeBackgrounds({
                top: readRenderedBackground(
                    wrapper?.previousElementSibling ||
                        current.previousElementSibling
                ),
                bottom: readRenderedBackground(
                    wrapper?.nextElementSibling || current.nextElementSibling
                ),
            });
        };

        frameId = window.requestAnimationFrame(updateRenderedBridgeBackgrounds);
        window.addEventListener('resize', updateRenderedBridgeBackgrounds);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener(
                'resize',
                updateRenderedBridgeBackgrounds
            );
        };
    }, [isBoxLayout, topBridgeColorScheme, bottomBridgeColorScheme]);

    // alignment presets
    let textAlign = 'text-center mx-auto max-w-4xl';
    let containerAlign = 'justify-center mx-auto';
    let buttonAlign = 'justify-center';
    let contentDirection = '';

    if (!isBoxLayout) {
        if (Alignment === 'left') {
            textAlign = 'text-center md:text-left max-w-3xl';
            containerAlign = 'justify-start';
            buttonAlign =
                'items-center justify-center md:items-start md:justify-start';
            contentDirection = 'md:flex-row';
        } else if (Alignment === 'right') {
            textAlign = 'text-center max-w-3xl md:text-left ml-auto';
            containerAlign = 'justify-end';
            buttonAlign =
                'items-center justify-center md:items-start md:justify-start';
            contentDirection = 'md:flex-row-reverse';
        }
    }

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const blurBoxMargin = isBoxLayout
        ? 'my-10 mx-6 lg:mx-8'
        : 'my-24 mx-6 lg:mx-8 lg:my-32';
    const blurBoxPadding = 'px-4 py-16 md:px-10 lg:px-8';

    // background image position
    const { x: mediaPosX, y: mediaPosY } = useMemo(
        () => parseImagePosition(ImagePosition),
        [ImagePosition]
    );
    const imageBackgroundStyles = useMemo(() => {
        if (!backgroundImageUrl || backgroundIsEmbed) return {};
        return { backgroundPosition: `${mediaPosX}% ${mediaPosY}%` };
    }, [backgroundImageUrl, backgroundIsEmbed, mediaPosX, mediaPosY]);
    const normalizedEmbedHtml = useMemo(
        () =>
            normalizeSvgEmbedMarkup(backgroundImageUrl, {
                x: mediaPosX,
                y: mediaPosY,
            }),
        [backgroundImageUrl, mediaPosX, mediaPosY]
    );
    const videoPositionStyles = {
        left: `${mediaPosX}%`,
        top: `${mediaPosY}%`,
        transform: `translate(-${mediaPosX}%, -${mediaPosY}%)`,
    };

    // computed colors for content box
    const effectiveTextColor = boxColor ? boxTextColor : textColor;
    const effectiveTaglineColor = boxColor ? boxTextColor : taglineColor;
    const effectiveThirdButton = boxColor ? boxThirdButton : thirdButton;
    const effectiveSecondaryButton = boxColor
        ? boxSecondaryButton
        : secondaryButton;
    const panelBackgroundColor = boxColor || backgroundColor;

    const preset = normalizeAnimation(Animation);
    const duration = Number(Duration) || 0.6;
    const normalizedAnimationGroup = normalizeAnimationGroup(AnimationGroup);
    const animationGroup =
        isBoxLayout && normalizedAnimationGroup === 'media'
            ? 'content'
            : normalizedAnimationGroup;

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = (e) => setPrefersReduced(e.matches);
        setPrefersReduced(mq.matches);
        mq.addEventListener?.('change', update);
        return () => mq.removeEventListener?.('change', update);
    }, []);

    const disableAnim = prefersReduced || preset === 'none';
    const animateContent =
        animationGroup === 'content' || animationGroup === 'all';
    const animateCTAs =
        animationGroup === 'ctas' ||
        animationGroup === 'all' ||
        animationGroup === 'content';
    const animateMedia = animationGroup === 'media' || animationGroup === 'all';
    const animateBox = isBoxLayout && animationGroup === 'box';
    const shouldFixBackground = isBackgroundFixed && !animateMedia;
    const fixedClass = shouldFixBackground ? 'lg:bg-fixed' : '';
    const embedPositionClass = shouldFixBackground
        ? 'absolute inset-0 lg:fixed lg:inset-0'
        : 'absolute inset-0';
    const viewport = useMemo(
        () => ({ once: !EditorPreview, amount: 0.5 }),
        [EditorPreview]
    );
    const baseKey = `cta-hero-${Layout}-${animationGroup}-${preset}-${duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const buildMotionProps = useMemo(() => {
        if (disableAnim) return () => ({});
        return () => {
            const variants = getVariantFromSimpleControls(preset, duration);
            if (!variants) return {};
            const hasOffscreen = 'offscreen' in variants;
            const initialState = hasOffscreen ? 'offscreen' : 'hidden';
            const targetState = hasOffscreen ? 'onscreen' : 'show';
            return EditorPreview
                ? { variants, initial: initialState, animate: targetState }
                : {
                      variants,
                      initial: initialState,
                      whileInView: targetState,
                      viewport,
                  };
        };
    }, [disableAnim, preset, duration, EditorPreview, viewport]);

    const buildMediaMotionProps = useMemo(() => {
        if (disableAnim) return () => ({});
        return () => {
            const d = Math.max(0.4, Number(duration) || 0.6);
            const baseVariants =
                preset === 'slide'
                    ? {
                          offscreen: { opacity: 0, y: 24, scale: 1.1 },
                          onscreen: {
                              opacity: 1,
                              y: 0,
                              transition: { type: 'tween', duration: d },
                          },
                      }
                    : preset === 'zoom'
                    ? {
                          offscreen: { opacity: 0, scale: 1.1 },
                          onscreen: {
                              opacity: 1,
                              scale: 1,
                              transition: { type: 'tween', duration: d },
                          },
                      }
                    : getVariantFromSimpleControls(preset, duration);
            if (!baseVariants) return {};
            const hasOffscreen = 'offscreen' in baseVariants;
            const initialState = hasOffscreen ? 'offscreen' : 'hidden';
            const targetState = hasOffscreen ? 'onscreen' : 'show';
            return EditorPreview
                ? {
                      variants: baseVariants,
                      initial: initialState,
                      animate: targetState,
                  }
                : {
                      variants: baseVariants,
                      initial: initialState,
                      whileInView: targetState,
                      viewport,
                  };
        };
    }, [disableAnim, preset, duration, EditorPreview, viewport]);

    const contentMotionProps = useMemo(
        () => (animateContent ? buildMotionProps() : {}),
        [animateContent, buildMotionProps]
    );

    const ctaMotionProps = useMemo(
        () => (animateCTAs ? buildMotionProps() : {}),
        [animateCTAs, buildMotionProps]
    );

    const mediaMotionProps = useMemo(
        () => (animateMedia ? buildMediaMotionProps() : {}),
        [animateMedia, buildMediaMotionProps]
    );

    const boxMotionProps = useMemo(
        () => (animateBox ? buildMotionProps() : {}),
        [animateBox, buildMotionProps]
    );

    const { hasAny: hasCTAs, element: CTAs } = getCTA(props, {
        secondaryVariant: effectiveSecondaryButton,
        outlineVariant: effectiveThirdButton,
        buttonAlign,
        wrapSpacing: isBoxLayout ? 'mt-9' : 'mt-10 lg:mt-16',
        className: 'w-full',
        buttonsClassName: isBoxLayout
            ? 'flex flex-wrap items-center justify-center gap-2'
            : 'flex flex-wrap items-center gap-2',
        enableAnim: false, // animation handled by MotionMaybe wrapper
    });

    // content box - Box layout renders inside the wide reference panel
    const content = isBoxLayout ? (
        isClear ? null : (
            <>
                <MotionMaybe
                    enable={animateContent && !disableAnim}
                    k={`${baseKey}-header`}
                    motionProps={contentMotionProps}
                >
                    <MarkDown
                        variant="h2"
                        className={`mx-auto max-w-[700px] break-words text-center font-display text-2xl font-bold uppercase leading-tight tracking-normal md:text-3xl lg:text-4xl ${effectiveTaglineColor}`}
                    >
                        {Header}
                    </MarkDown>
                </MotionMaybe>
                {Body && (
                    <MotionMaybe
                        enable={animateContent && !disableAnim}
                        k={`${baseKey}-body`}
                        motionProps={contentMotionProps}
                        className="mt-6"
                    >
                        <MarkDown
                            className={`mx-auto max-w-4xl text-center text-base font-text leading-7 md:text-lg md:leading-8 ${effectiveTextColor}`}
                        >
                            {Body}
                        </MarkDown>
                    </MotionMaybe>
                )}
                {hasCTAs && (
                    <MotionMaybe
                        enable={animateCTAs && !disableAnim}
                        k={`${baseKey}-ctas`}
                        motionProps={ctaMotionProps}
                        className="mt-4"
                    >
                        {CTAs}
                    </MotionMaybe>
                )}
            </>
        )
    ) : isClear ? null : (
        // Full layout: with box container
        <MotionMaybe
            enable={animateContent && !disableAnim}
            k={`${baseKey}-box`}
            motionProps={contentMotionProps}
            className={`${boxColor} ${blurBoxPadding} ${boxBorder} ${blurBoxMargin} rounded-[min(1.5rem,var(--shape-radius))]`}
        >
            <div className="flex flex-col flex-wrap gap-4 px-2">
                <MotionMaybe
                    enable={animateContent && !disableAnim}
                    k={`${baseKey}-header`}
                    motionProps={contentMotionProps}
                >
                    <MarkDown
                        variant="h2"
                        className={`break-words font-display text-4xl tracking-tight md:text-5xl lg:text-6xl ${effectiveTaglineColor}`}
                    >
                        {Header}
                    </MarkDown>
                </MotionMaybe>
                {Body && (
                    <MotionMaybe
                        enable={animateContent && !disableAnim}
                        k={`${baseKey}-body`}
                        motionProps={contentMotionProps}
                    >
                        <MarkDown
                            className={`mt-6 text-base font-text leading-7 md:text-lg md:leading-8 ${effectiveTextColor} ${textAlign}`}
                        >
                            {Body}
                        </MarkDown>
                    </MotionMaybe>
                )}
            </div>

            <div className="px-2">
                {hasCTAs && (
                    <MotionMaybe
                        enable={animateCTAs && !disableAnim}
                        k={`${baseKey}-ctas`}
                        motionProps={ctaMotionProps}
                    >
                        {CTAs}
                    </MotionMaybe>
                )}
            </div>
        </MotionMaybe>
    );

    return (
        <section
            ref={sectionRef}
            className={`editable-component relative h-auto w-full ${
                isBoxLayout ? '' : backgroundColor
            }`}
            style={isBoxLayout ? undefined : backgroundStyle}
        >
            {isBoxLayout && (
                <div className="pointer-events-none absolute inset-0 z-0">
                    <div
                        className={`h-1/2 w-full ${topBridgeBackgroundColor}`}
                        style={
                            renderedBridgeBackgrounds.top ||
                            topBridgeBackgroundStyle
                        }
                    />
                    <div
                        className={`h-1/2 w-full ${bottomBridgeBackgroundColor}`}
                        style={
                            renderedBridgeBackgrounds.bottom ||
                            bottomBridgeBackgroundStyle
                        }
                    />
                </div>
            )}
            <div
                className={`${minHeightClass} relative isolate z-10 ${
                    isBoxLayout
                        ? `px-4 ${vSpace} sm:px-6 lg:px-8`
                        : 'min-h-[300px]'
                } overflow-hidden`}
            >
                {/* Background layer - hidden for Box layout */}
                {!isBoxLayout && (
                    <MotionMaybe
                        enable={animateMedia && !disableAnim}
                        k={`${baseKey}-media`}
                        motionProps={mediaMotionProps}
                        className="absolute inset-0 -z-10 overflow-hidden"
                    >
                        {/* server fallback: image only; client: video if available, else image */}
                        {showVideo ? (
                            <BackgroundVideo
                                videoUrl={videoUrl}
                                videoPositionStyles={videoPositionStyles}
                                onVideoError={() => setVideoError(true)}
                            />
                        ) : backgroundIsEmbed ? (
                            <div className="pointer-events-none absolute inset-0 h-full w-full overflow-hidden">
                                <div
                                    className={`${embedPositionClass} [&>*]:h-full [&>*]:max-h-none [&>*]:w-full [&>*]:max-w-none [&>svg]:block`}
                                    dangerouslySetInnerHTML={{
                                        __html: normalizedEmbedHtml,
                                    }}
                                />
                            </div>
                        ) : (
                            backgroundImageUrl && (
                                <div
                                    className={`absolute inset-0 h-full w-full bg-cover bg-center ${fixedClass}`}
                                    style={{
                                        backgroundImage: `url(${backgroundImageUrl})`,
                                        ...imageBackgroundStyles,
                                    }}
                                />
                            )
                        )}
                        {/* overlay */}
                        <div
                            className={`absolute inset-0 ${overlayColor}`}
                            style={overlayStyle}
                        />
                    </MotionMaybe>
                )}

                {/* Foreground content */}
                <div
                    className={`relative z-40 mx-auto ${
                        isBoxLayout ? 'max-w-[1000px]' : 'max-w-screen-2xl'
                    } ${containerAlign}`}
                >
                    {isBoxLayout ? (
                        <MotionMaybe
                            enable={animateBox && !disableAnim}
                            k={`${baseKey}-box-panel`}
                            motionProps={boxMotionProps}
                            className={`flex min-h-[260px] flex-col items-center justify-center rounded-[24px] ${boxBorder} ${panelBackgroundColor} px-6 py-16 text-center shadow-[0_28px_45px_rgba(0,0,0,0.55)] sm:px-10 md:min-h-[320px] lg:px-16`}
                            style={boxColor ? undefined : backgroundStyle}
                        >
                            {content}
                        </MotionMaybe>
                    ) : (
                        <div className={`relative ${contentDirection}`}>
                            <div className={textAlign}>
                                <div className={`${vSpace} relative`}>
                                    {content}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
